import { Injectable } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { CartActions, CartSelectors, LocationSilo, Silo } from '@next/core-lib/cart';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { BCP47Locale, I18nActions, I18nSelectors, LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { RouterSelectors } from '@next/core-lib/router-store';

import { SelectedLocation, UserActions, UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, concatMap, EMPTY, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, retry, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import * as AppActions from './actions';
import { repeatOrdersRequestSuccess } from './actions';
import { AddToCartComponent } from '../components/add-to-cart/add-to-cart.component';
import { OrderDetailsComponent } from '../components/order-details/order-details.component';
import { RepeatOrderDeliveryDateComponent } from '../components/repeat-order/components/delivery-date/delivery-date.component';
import { RepeatOrderSiloDistributionComponent } from '../components/repeat-order/components/silo/silo.component';
import { RepeatOrderAggregationRequest } from '../components/repeat-order/pages/abstract-repeat-order-page-component.directive';
import { EditSiloDialogComponent } from '../components/silos/components/edit-silo/edit-silo.component';
// eslint-disable-next-line max-len
import { SiloProductSupplementsWizardComponent } from '../components/silos/components/silo-product-supplements-wizard/silo-product-supplements-wizard.component';
import { CatalogItem } from '../models/catalog-item.model';
import { RepeatOrderItem } from '../models/repeat-order-item.model';
import { RepeatOrderItemResponse, RepeatOrderItemSiloResponse } from '../models/repeat-order.response';
import * as ProductActions from '../product/store/actions';
import { CartOrderApiService } from '../services/cart-order-api.service';
import { ElasticApiService } from '../services/elastic-api.service';
import { OrderHistoryService } from '../services/order-history.service';
import { SiloApiService } from '../services/silo-api.service';

@Injectable()
export class AppEffects {
  private readonly currentUrl$ = this.store.select(RouterSelectors.getUrl).pipe(startWith(`${globalThis.location.pathname}${globalThis.location.search}`));

  localeOrLocationChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(I18nActions.setSelectedLocale, UserActions.locationInitialized, UserActions.locationChanged),
      switchMap((_) => of(AppActions.catalogCategoriesRequest({ isRoot: true }))),
    ),
  );

  getFavoritesOnLocationChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized, UserActions.locationChanged),
      withLatestFrom(this.store.select(UserSelectors.selectProfileId), this.currentUrl$),
      filter(([_, profileId, currentUrl]) => !!profileId && !this.isSelectRelationshipRoute(currentUrl)),
      map(([_, profileId]) => ProductActions.getFavorites({ profileId })),
    ),
  );

  ordersRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.ordersRequest),
      switchMap(({ itemsToDisplay }) =>
        this.cartOrderApiService.getOrders(itemsToDisplay).pipe(
          map(({ orders }) => AppActions.ordersSuccess({ orders })),
          catchError(() => of(AppActions.ordersFailure())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  orderDetailsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.orderDetailsRequest),
      switchMap(({ id }) =>
        this.cartOrderApiService.getOrder(id).pipe(
          map((orderDetails) => AppActions.orderDetailsSuccess({ orderDetails })),
          catchError(() => of(AppActions.orderDetailsFailure())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  catalogCategoriesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.catalogCategoriesRequest),
      withLatestFrom(this.store.select(I18nSelectors.selectLocale), this.store.select(UserSelectors.selectLocation)),
      filter(([_, locale, location]: [{ isRoot: boolean }, BCP47Locale, SelectedLocation]) => !!locale && !!location),
      switchMap(([actionData, locale, location]: [{ isRoot: boolean }, BCP47Locale, SelectedLocation]) =>
        this.elasticApiService.getCatalog(location.salesAreas, locale, actionData.isRoot).pipe(
          map((catalogCategories) =>
            AppActions.catalogCategoriesSuccess({
              catalogCategories: catalogCategories.catalogItems,
            }),
          ),
          catchError(() => of(AppActions.catalogCategoriesFailure())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  catalogSubcategoriesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.setSelectedCatalogCategory),
      withLatestFrom(this.store.select(I18nSelectors.selectLocale), this.store.select(UserSelectors.selectLocation)),
      filter(([{ selectedCatalogCategory }, locale, location]) => !!selectedCatalogCategory && !!locale && !!location),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([{ selectedCatalogCategory }, locale, location]: [{ selectedCatalogCategory: CatalogItem }, BCP47Locale, SelectedLocation]) =>
        this.elasticApiService.getCatalog(location.salesAreas, locale, false, selectedCatalogCategory.entryId, null).pipe(
          map((elasticApiResult) =>
            AppActions.catalogSubcategoriesSuccess({
              catalogSubcategories: elasticApiResult.catalogItems,
            }),
          ),
          catchError(() => of(AppActions.catalogSubcategoriesFailure())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  totalNumberOfProductsFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.setCatalogProductSearchString),
      withLatestFrom(this.store.select(I18nSelectors.selectLocale), this.store.select(UserSelectors.selectLocation)),
      filter(([, locale, location]) => !!locale && !!location),
      switchMap(([{ catalogProductSearchString }, locale, location]) => {
        if (!catalogProductSearchString) {
          return of(
            AppActions.setCatalogTotalNumberOfProductsFound({
              catalogTotalNumberOfProductsFound: null,
            }),
          );
        }
        return this.elasticApiService.getSkuCatalogCount(catalogProductSearchString, locale, location.salesAreas[0].salesOrganization).pipe(
          map((result) =>
            AppActions.setCatalogTotalNumberOfProductsFound({
              catalogTotalNumberOfProductsFound: result,
            }),
          ),
        );
      }),
    ),
  );

  catalogItemRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.catalogItemRequest),
      withLatestFrom(this.store.select(I18nSelectors.selectLocale), this.store.select(UserSelectors.selectLocation)),
      filter(([_, locale, location]) => !!locale && !!location),
      switchMap(([{ id }, locale, location]: [{ id: string }, BCP47Locale, SelectedLocation]) =>
        this.elasticApiService.getCatalogItem(location.salesAreas, locale, id).pipe(
          map((catalogItem) => AppActions.catalogItemRequestSuccess({ catalogItem })),
          catchError(() => of(AppActions.catalogItemRequestFailure())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  openAddToCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openAddToCart),
      switchMap(({ catalogItem }) =>
        this.dialogService.fullscreenDialog(
          AddToCartComponent,
          {
            title: catalogItem.title,
            subTitle: catalogItem.externalSkuId,
            confirmButton: true,
            confirmButtonText: this.localeService.translate('add-to-cart.add-to-cart'),
            catalogItem,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      withLatestFrom(this.store.select(CartSelectors.selectCurrentSalesOrderCart)),
      map(
        ([{ status, result }, cart]) =>
          status === DIALOG_RETURN_TYPES.confirm &&
          CartActions.addCartLineToCart({
            cartLine: {
              skuId: result.catalogItem.id,
              quantity: result.quantity,
            },
            cartId: cart?.id ?? undefined,
          }),
      ),
      filter((action) => !!action),
    ),
  );

  openOrderDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openOrderDetails),
      switchMap(() =>
        this.dialogService.fullscreenDialog(
          OrderDetailsComponent,
          {
            cancelButton: true,
            altButton: false,
            confirmButton: false,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(AppActions.clearOrderDetailsRequest())),
    ),
  );

  openEditSilo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openEditSiloDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          EditSiloDialogComponent,
          {
            silo: actionData.silo,
            cancelButton: true,
            altButton: false,
            confirmButton: false,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(AppActions.openEditSiloDialogSuccess())),
    ),
  );

  addSilo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.addSiloRequest),
      concatMap((actionData) =>
        this.siloApiService.upsertSilo(actionData.silo).pipe(
          map((silo) => ProductActions.addSilo({ silo })),
          tap(() =>
            this.store.dispatch(
              NotificationActions.successSnackbar({
                message: this.localeService.translate('edit-silo.confirmationMessage'),
              }),
            ),
          ),
          catchError(() => of(NotificationActions.errorSnackbar({ message: this.localeService.translate('edit-silo.errorMessage') }))),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  updateSilo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updateSiloRequest),
      concatMap((actionData) =>
        this.siloApiService.upsertSilo(actionData.silo).pipe(
          map((silo) => ProductActions.updateSilo({ id: silo.id, silo })),
          tap(() =>
            this.store.dispatch(
              NotificationActions.successSnackbar({
                message: this.localeService.translate('edit-silo.confirmationMessage'),
              }),
            ),
          ),
          catchError(() => of(NotificationActions.errorSnackbar({ message: this.localeService.translate('edit-silo.errorMessage') }))),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteSilo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteSiloRequest),
      concatMap((actionData) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('silos.deleteSiloTitle'),
            confirmButtonText: this.localeService.translate('silos.deleteSiloConfirmText'),
            text: this.localeService.translate('silos.deleteConfirmation', {
              siloNumber: actionData.silo.siloNumber,
            }),
          })
          .pipe(
            concatMap(
              ({ status }) =>
                (status === DIALOG_RETURN_TYPES.confirm &&
                  this.siloApiService.deleteSilo(actionData.silo.id).pipe(
                    map(() => ProductActions.deleteSilo({ id: actionData.silo.id })),
                    tap(() =>
                      this.store.dispatch(
                        NotificationActions.successSnackbar({
                          message: this.localeService.translate('silos.deleteSiloSuccess'),
                        }),
                      ),
                    ),
                    catchError(() => of(NotificationActions.errorSnackbar({ message: this.localeService.translate('silos.deleteSiloError') }))),
                    startWith(CoreActions.setLoading({ loading: true })),
                    finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                  )) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteSiloProductListItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteSiloProductListItemRequest),
      concatMap((actionData) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('silo-product.deleteSiloProductTitle'),
            confirmButtonText: this.localeService.translate('silo-product.deleteSiloProductConfirmText'),
            text: this.localeService.translate('silo-product.deleteConfirmation', {
              productName: actionData.siloProductItem.title,
            }),
          })
          .pipe(
            concatMap(
              ({ status }) =>
                (status === DIALOG_RETURN_TYPES.confirm &&
                  this.siloApiService.deleteProductListItem(actionData.siloId, actionData.siloProductItem.id).pipe(
                    map(() => ProductActions.deleteSiloProductListItem({ siloId: actionData.siloId, productId: actionData.siloProductItem.id })),
                    tap(() =>
                      this.store.dispatch(
                        NotificationActions.successSnackbar({
                          message: this.localeService.translate('silo-product.deleteSiloProductSuccess'),
                        }),
                      ),
                    ),
                    catchError(() => of(NotificationActions.errorSnackbar({ message: this.localeService.translate('silo-product.deleteSiloProductError') }))),
                    startWith(CoreActions.setLoading({ loading: true })),
                    finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                  )) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  openSiloProductDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openSiloProductSupplementsDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          SiloProductSupplementsWizardComponent,
          {
            siloId: actionData.siloId,
            siloProduct: actionData.siloProduct,
            cancelButton: true,
          },
          {
            autoFocus: true,
          },
        ),
      ),
      map(() => AppActions.openSiloProductSupplementsDialogSuccess()),
      switchMap(() => of(ProductActions.resetVariants(), ProductActions.resetSelectedProductSiloLine())),
    ),
  );

  repeatOrdersRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.repeatOrdersRequest),
      switchMap(({ aggregationType }) =>
        combineLatest([
          this.store.select(UserSelectors.selectLocation).pipe(
            filter((location): location is SelectedLocation => !!location),
            take(1),
          ),
          this.currentUrl$.pipe(take(1)),
          of(aggregationType),
        ]),
      ),
      debounceTime(1),
      switchMap(([location, _, aggregationType]) =>
        combineLatest([
          this.orderHistoryService.get(location.id, location.salesAreas[0].salesOrganization).pipe(
            retry(3),
            catchError(() => of({ bulk: [], generalCargo: [] })),
          ),
          this.siloApiService.getSilos().pipe(catchError(() => of({ silos: Array<LocationSilo>() }))),
          of(aggregationType),
        ]),
      ),
      map(([{ bulk, generalCargo }, { silos }, aggregationType]) => ({
        generalCargo: generalCargo.map((orderItem: RepeatOrderItemResponse) => this.mapRepeatOrderItemHistory(orderItem, silos)),
        bulk: bulk.map((orderItem: RepeatOrderItemResponse) => this.mapRepeatOrderItemHistory(orderItem, silos)),
        aggregationType,
      })),
      map(({ bulk, generalCargo, aggregationType }) => repeatOrdersRequestSuccess({ bulk, generalCargo, aggregationType })),
    ),
  );

  repeatOrdersRequestSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.repeatOrdersRequestSuccess),
      switchMap(({ bulk, generalCargo, aggregationType }) => {
        switch (aggregationType) {
          case RepeatOrderAggregationRequest.bulk:
            return of(
              ProductActions.getProductInformationsRequest({
                skuIds: this.parseSkuIds([...bulk]),
              }),
              ProductActions.getDeliveryProposalsRequest({
                skuIds: this.parseSkuIds([...bulk]),
              }),
            );
          case RepeatOrderAggregationRequest.generalCargo:
            return of(
              ProductActions.getProductInformationsRequest({
                skuIds: this.parseSkuIds([...generalCargo]),
              }),
              ProductActions.getDeliveryProposalsRequest({
                skuIds: this.parseSkuIds([...generalCargo]),
              }),
            );
          case RepeatOrderAggregationRequest.none:
          default:
            return EMPTY;
        }
      }),
    ),
  );

  openRepeatOrderSiloDistributionDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openRepeatOrderSiloDistributionDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          RepeatOrderSiloDistributionComponent,
          {
            repeatOrderItem: actionData.repeatOrderItem,
            quantity: actionData.quantity,
            cancelButton: true,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(AppActions.openRepeatOrderSiloDistributionDialogSuccess())),
    ),
  );

  openRepeatOrderDeliveryDateDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openRepeatOrderDeliveryDateDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          RepeatOrderDeliveryDateComponent,
          {
            repeatOrderItem: actionData.repeatOrderItem,
            repeatOrderType: actionData.repeatOrderType,
            deliveryInformation: actionData.deliveryInformation,
            cancelButton: true,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(AppActions.openRepeatOrderDeliveryDateDialogSuccess())),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly cartOrderApiService: CartOrderApiService,
    private readonly elasticApiService: ElasticApiService,
    private readonly dialogService: DialogService,
    private readonly siloApiService: SiloApiService,
    private readonly orderHistoryService: OrderHistoryService,
  ) {}

  private readonly parseSkuIds = (repeatOrderItems: RepeatOrderItem[]): number[] => repeatOrderItems.map((item) => Number(item.internalId));

  private isSelectRelationshipRoute(url?: string | null): boolean {
    return (url ?? '').startsWith('/select-relationship');
  }

  private mapRepeatOrderItemHistory(item: RepeatOrderItemResponse, locationSilos: LocationSilo[]): RepeatOrderItem {
    const silos: Silo[] =
      item.silos.length === 1 ? this.mapRepeatOrderSingleSilo(item.silos[0], locationSilos) : this.mapRepeatOrderSilos(item.silos, locationSilos);
    const lastUsedSilos = silos;
    return { ...item, silos, lastUsedSilos };
  }

  private mapRepeatOrderSingleSilo(historicSiloUsage: RepeatOrderItemSiloResponse, locationSilos: LocationSilo[]): Silo[] {
    const locationSilo: LocationSilo = locationSilos.find((ls) => ls.siloNumber === historicSiloUsage.siloId);
    // For a better UX, we put order lines with a single silo and the distributiontype exact to full.
    // This way the user doesn't need to update distributiontype when increasing/decreasing quantity.
    const useDistributionTypeFull = historicSiloUsage.distributionType === 'Exact';
    return locationSilo
      ? [
          {
            id: locationSilo.id,
            name: locationSilo.siloNumber,
            quantity: useDistributionTypeFull ? null : historicSiloUsage.quantity,
            siloNumber: locationSilo.siloNumber,
            distributionType: useDistributionTypeFull ? 'Full' : historicSiloUsage.distributionType,
          } as Silo,
        ]
      : [];
  }

  private mapRepeatOrderSilos(historicSiloUsage: RepeatOrderItemSiloResponse[], locationSilos: LocationSilo[]): Silo[] {
    const historicallyUsedSiloIds = historicSiloUsage.map((usedSilo) => usedSilo.siloId);
    const existingLocationSilos: LocationSilo[] = locationSilos.filter((ls) => historicallyUsedSiloIds.includes(ls.siloNumber));
    return existingLocationSilos.map((ls) => {
      const siloUsage = historicSiloUsage.find((silo) => silo.siloId === ls.siloNumber);
      return {
        id: ls.id,
        name: ls.siloNumber,
        quantity: siloUsage.distributionType === 'Exact' ? siloUsage.quantity : null,
        siloNumber: ls.siloNumber,
        distributionType: siloUsage.distributionType,
      } as Silo;
    });
  }
}
